import React, { useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom';
import Loader from '../../Components/Loader';
import { API_BASE, fetchApiJson } from '../../config/apiBase';

const AgentDashboard = () => {
  const navigate = useNavigate();

  const currentUser = useMemo(() => {
    try {
      const rawUser = localStorage.getItem("user");
      return rawUser ? JSON.parse(rawUser) : null;
    } catch {
      return null;
    }
  }, []);

  const currentAgentId = currentUser?.id || currentUser?._id || currentUser?.userId || "";
  const currentRole = String(currentUser?.role || "").toUpperCase();
  const isGalleryManager = currentRole === "ADMIN" || currentRole === "AGENT";
  const profileCacheKey = currentAgentId ? `qtech_agent_profile_${currentAgentId}` : "";
  const displayName = currentUser?.name || currentUser?.fullName || "Agent";
  const buildProfileForm = React.useCallback(
    (data = {}, fallback = {}) => ({
      fullName: data.fullName || fallback.fullName || fallback.name || "",
      email: data.email || fallback.email || "",
      phone: data.phone || fallback.phone || "",
      companyName: data.companyName || fallback.companyName || "",
      licenseNumber: data.licenseNumber || fallback.licenseNumber || "",
      bio: data.bio || fallback.bio || "",
      address: data.address || fallback.address || "",
      profileImageUrl: data.profileImageUrl || fallback.profileImageUrl || "",
    }),
    []
  );
  const readCachedProfile = React.useCallback(() => {
    if (!profileCacheKey) return null;
    try {
      const raw = localStorage.getItem(profileCacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [profileCacheKey]);
  const writeCachedProfile = React.useCallback(
    (profile) => {
      if (!profileCacheKey) return;
      try {
        localStorage.setItem(profileCacheKey, JSON.stringify(profile));
      } catch {
        // ignore storage quota/privacy mode errors
      }
    },
    [profileCacheKey]
  );
  const [assignedProperties, setAssignedProperties] = React.useState([]);
  const [propertyHistory, setPropertyHistory] = React.useState([]);
  const [loadingAssigned, setLoadingAssigned] = React.useState(false);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [finishingPropertyId, setFinishingPropertyId] = React.useState("");
  const [assignmentError, setAssignmentError] = React.useState("");
  const [profileLoading, setProfileLoading] = React.useState(false);
  const [profileSaving, setProfileSaving] = React.useState(false);
  const [profileUploading, setProfileUploading] = React.useState(false);
  const [profileFeedback, setProfileFeedback] = React.useState({ type: "", message: "" });
  const [profileForm, setProfileForm] = React.useState({
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    licenseNumber: "",
    bio: "",
    address: "",
    profileImageUrl: "",
  });
  const [galleryItems, setGalleryItems] = React.useState([]);
  const [galleryLoading, setGalleryLoading] = React.useState(false);
  const [gallerySaving, setGallerySaving] = React.useState(false);
  const [galleryUploading, setGalleryUploading] = React.useState(false);
  const [galleryFeedback, setGalleryFeedback] = React.useState({ type: "", message: "" });
  const [editingGalleryId, setEditingGalleryId] = React.useState("");
  const [galleryForm, setGalleryForm] = React.useState({
    title: "",
    description: "",
    category: "Property",
    sortOrder: 0,
    isPublished: true,
    imageUrls: [],
    coverImageUrl: "",
  });

  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
      return;
    }

    if (!["AGENT", "ADMIN"].includes(String(currentUser.role || "").toUpperCase())) {
      navigate("/");
    }
  }, [currentUser, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("qtech-auth-changed"));
    }
    navigate("/login");
  };

  const resolvePropertyImage = React.useCallback((url) => {
    if (!url) return "/img/property-placeholder.svg";
    if (/^data:image\//i.test(url)) return url;
    if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/uploads\//i.test(url)) {
      return `${API_BASE}${new URL(url).pathname}`;
    }
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
    return url.startsWith("/") ? url : `/${url}`;
  }, []);

  const resolveProfileImage = React.useCallback((url) => {
    if (!url) return "/img/blog/author.jpg";
    if (/^data:image\//i.test(url)) return url;
    if (/^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/uploads\//i.test(url)) {
      return `${API_BASE}${new URL(url).pathname}`;
    }
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith("/uploads/")) return `${API_BASE}${url}`;
    return url.startsWith("/") ? url : `/${url}`;
  }, []);

  const handlePropertyImageFallback = React.useCallback((event) => {
    if (event.currentTarget.src.includes("/img/property-placeholder.svg")) return;
    event.currentTarget.src = "/img/property-placeholder.svg";
  }, []);

  const handleProfileImageFallback = React.useCallback((event) => {
    if (event.currentTarget.src.includes("/img/blog/author.jpg")) return;
    event.currentTarget.src = "/img/blog/author.jpg";
  }, []);

  const getAuthHeaders = React.useCallback(() => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const fetchAuthApiJson = React.useCallback(async (path, init = {}) => {
    const authBase = (localStorage.getItem("qtech_auth_api_base") || API_BASE).replace(/\/+$/, "");
    const cleanedPath = path.startsWith("/") ? path : `/${path}`;
    const response = await fetch(`${authBase}${cleanedPath}`, init);
    const contentType = response.headers.get("content-type") || "";
    const json = contentType.includes("application/json") ? await response.json() : null;

    if (!response.ok) {
      throw new Error(json?.message || `Request failed with status ${response.status}`);
    }

    return { response, json, base: authBase };
  }, []);

  const loadAssignedProperties = React.useCallback(async () => {
    if (!currentAgentId) return;
    try {
      setLoadingAssigned(true);
      setAssignmentError("");
      const { json } = await fetchApiJson(`/api/agents/${currentAgentId}/assigned-properties`);
      setAssignedProperties(Array.isArray(json?.data) ? json.data : []);
    } catch (error) {
      setAssignedProperties([]);
      setAssignmentError(error?.message || "Failed to load assigned properties.");
    } finally {
      setLoadingAssigned(false);
    }
  }, [currentAgentId]);

  const loadPropertyHistory = React.useCallback(async () => {
    if (!currentAgentId) return;
    try {
      setLoadingHistory(true);
      const { json } = await fetchApiJson(`/api/agents/${currentAgentId}/property-history`);
      setPropertyHistory(Array.isArray(json?.data) ? json.data : []);
    } catch {
      setPropertyHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [currentAgentId]);

  useEffect(() => {
    if (!currentAgentId || currentRole === "ADMIN") return;
    loadAssignedProperties();
    loadPropertyHistory();
  }, [currentAgentId, currentRole, loadAssignedProperties, loadPropertyHistory]);

  const loadAgentProfile = React.useCallback(async () => {
    if (!currentAgentId) return;
    const cachedProfile = readCachedProfile();
    if (cachedProfile) {
      setProfileForm(buildProfileForm(cachedProfile, currentUser || {}));
    }
    try {
      setProfileLoading(true);
      const { json } = await fetchApiJson(`/api/agents/${currentAgentId}/profile`);
      const serverProfile = json?.data || {};
      const mergedProfile = buildProfileForm(serverProfile, cachedProfile || currentUser || {});
      setProfileForm(mergedProfile);
      writeCachedProfile(mergedProfile);
    } catch (error) {
      if (!cachedProfile) {
        setProfileFeedback({
          type: "error",
          message: error?.message || "Failed to load account details.",
        });
      }
    } finally {
      setProfileLoading(false);
    }
  }, [buildProfileForm, currentAgentId, currentUser, readCachedProfile, writeCachedProfile]);

  useEffect(() => {
    if (!currentAgentId) return;
    loadAgentProfile();
  }, [currentAgentId, loadAgentProfile]);

  const loadGalleryItems = React.useCallback(async () => {
    if (!isGalleryManager) return;
    try {
      setGalleryLoading(true);
      setGalleryFeedback({ type: "", message: "" });
      const { json } = await fetchAuthApiJson("/api/admin/gallery", {
        headers: getAuthHeaders(),
      });
      setGalleryItems(Array.isArray(json?.data) ? json.data : []);
    } catch (error) {
      setGalleryItems([]);
      setGalleryFeedback({ type: "error", message: error?.message || "Failed to load gallery items." });
    } finally {
      setGalleryLoading(false);
    }
  }, [fetchAuthApiJson, getAuthHeaders, isGalleryManager]);

  useEffect(() => {
    loadGalleryItems();
  }, [loadGalleryItems]);

  const resetGalleryForm = React.useCallback(() => {
    setEditingGalleryId("");
    setGalleryForm({
      title: "",
      description: "",
      category: "Property",
      sortOrder: 0,
      isPublished: true,
      imageUrls: [],
      coverImageUrl: "",
    });
  }, []);

  const handleGalleryChange = (event) => {
    const { name, type, checked, value } = event.target;
    setGalleryForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleGalleryImageUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    try {
      setGalleryUploading(true);
      setGalleryFeedback({ type: "", message: "" });
      const formData = new FormData();
      files.forEach((file) => formData.append("image", file));
      const { json } = await fetchAuthApiJson("/api/uploads/image", {
        method: "POST",
        body: formData,
      });
      const uploadedImages = Array.isArray(json?.data?.images)
        ? json.data.images
        : json?.data?.imageUrl
          ? [json.data]
          : [];
      const imageUrls = uploadedImages.map((image) => image.imageUrl).filter(Boolean);
      if (!imageUrls.length) throw new Error("Image upload failed.");
      setGalleryForm((prev) => ({
        ...prev,
        imageUrls: [...prev.imageUrls, ...imageUrls],
        coverImageUrl: prev.coverImageUrl || imageUrls[0],
      }));
      setGalleryFeedback({ type: "success", message: `${imageUrls.length} image(s) uploaded. Submit to publish.` });
    } catch (error) {
      setGalleryFeedback({ type: "error", message: error?.message || "Failed to upload gallery images." });
    } finally {
      setGalleryUploading(false);
      event.target.value = "";
    }
  };

  const removeGalleryImage = (indexToRemove) => {
    setGalleryForm((prev) => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, index) => index !== indexToRemove),
      coverImageUrl:
        prev.imageUrls[indexToRemove] === prev.coverImageUrl
          ? prev.imageUrls.filter((_, index) => index !== indexToRemove)[0] || ""
          : prev.coverImageUrl,
    }));
  };

  const setPrimaryGalleryImage = (url) => {
    setGalleryForm((prev) => ({ ...prev, coverImageUrl: url }));
  };

  const startEditGalleryItem = (item) => {
    const imageUrls = Array.isArray(item.imageUrls) && item.imageUrls.length ? item.imageUrls : [item.coverImageUrl].filter(Boolean);
    setEditingGalleryId(item.id || "");
    setGalleryForm({
      title: item.title || "",
      description: item.description || "",
      category: item.category || "Property",
      sortOrder: item.sortOrder || 0,
      isPublished: Boolean(item.isPublished),
      imageUrls,
      coverImageUrl: item.coverImageUrl || imageUrls[0] || "",
    });
    setGalleryFeedback({ type: "", message: "" });
  };

  const handleGallerySubmit = async (event) => {
    event.preventDefault();
    if (!isGalleryManager) return;
    try {
      setGallerySaving(true);
      setGalleryFeedback({ type: "", message: "" });
      const endpoint = editingGalleryId ? `/api/admin/gallery/${editingGalleryId}` : "/api/admin/gallery";
      const { json } = await fetchAuthApiJson(endpoint, {
        method: editingGalleryId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          ...galleryForm,
          coverImageUrl: galleryForm.coverImageUrl || galleryForm.imageUrls[0],
        }),
      });
      const wasEditing = Boolean(editingGalleryId);
      if (wasEditing) {
        setGalleryItems((prev) => prev.map((item) => (item.id === editingGalleryId ? json.data : item)).filter(Boolean));
      } else {
        setGalleryItems((prev) => [json.data, ...prev].filter(Boolean));
      }
      resetGalleryForm();
      setGalleryFeedback({
        type: "success",
        message: wasEditing ? "Gallery item updated successfully." : "Gallery item published successfully.",
      });
      await loadGalleryItems();
    } catch (error) {
      setGalleryFeedback({ type: "error", message: error?.message || "Failed to save gallery item." });
    } finally {
      setGallerySaving(false);
    }
  };

  const toggleGalleryPublish = async (item) => {
    if (!isGalleryManager || !item?.id) return;
    try {
      await fetchAuthApiJson(`/api/admin/gallery/${item.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ isPublished: !item.isPublished }),
      });
      await loadGalleryItems();
    } catch (error) {
      setGalleryFeedback({ type: "error", message: error?.message || "Failed to update gallery item." });
    }
  };

  const deleteGalleryItem = async (itemId) => {
    if (!isGalleryManager || !itemId) return;
    try {
      await fetchAuthApiJson(`/api/admin/gallery/${itemId}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      setGalleryItems((prev) => prev.filter((item) => item.id !== itemId));
      if (editingGalleryId === itemId) resetGalleryForm();
      setGalleryFeedback({ type: "success", message: "Gallery item deleted." });
    } catch (error) {
      setGalleryFeedback({ type: "error", message: error?.message || "Failed to delete gallery item." });
    }
  };

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfileImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setProfileUploading(true);
      setProfileFeedback({ type: "", message: "" });
      const formData = new FormData();
      formData.append("image", file);
      const { json } = await fetchApiJson("/api/uploads/image", {
        method: "POST",
        body: formData,
      });
      const imageUrl = json?.data?.imageUrl || "";
      if (!imageUrl) throw new Error("Image upload failed.");
      setProfileForm((prev) => ({ ...prev, profileImageUrl: imageUrl }));
      setProfileFeedback({ type: "success", message: "Profile image uploaded. Save changes to apply." });
    } catch (error) {
      setProfileFeedback({
        type: "error",
        message: error?.message || "Failed to upload profile image.",
      });
    } finally {
      setProfileUploading(false);
      event.target.value = "";
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    if (!currentAgentId) return;
    try {
      setProfileSaving(true);
      setProfileFeedback({ type: "", message: "" });
      const payload = {
        fullName: profileForm.fullName,
        email: profileForm.email,
        phone: profileForm.phone,
        companyName: profileForm.companyName,
        licenseNumber: profileForm.licenseNumber,
        bio: profileForm.bio,
        address: profileForm.address,
        profileImageUrl: profileForm.profileImageUrl,
      };
      const { json } = await fetchApiJson(`/api/agents/${currentAgentId}/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const updated = json?.data || {};
      const nextProfile = {
        ...payload,
        ...updated,
        fullName: updated.fullName || payload.fullName,
        email: updated.email || payload.email,
        phone: updated.phone || payload.phone,
        companyName: updated.companyName || payload.companyName,
        licenseNumber: updated.licenseNumber || payload.licenseNumber,
        bio: updated.bio || payload.bio,
        address: updated.address || payload.address,
        profileImageUrl: updated.profileImageUrl || payload.profileImageUrl,
      };
      setProfileForm((prev) => ({
        ...prev,
        ...nextProfile,
      }));
      writeCachedProfile(nextProfile);

      try {
        const rawUser = localStorage.getItem("user");
        const parsed = rawUser ? JSON.parse(rawUser) : {};
        localStorage.setItem(
          "user",
          JSON.stringify({
            ...parsed,
            name: updated.fullName || parsed?.name || parsed?.fullName,
            fullName: updated.fullName || parsed?.fullName || parsed?.name,
            phone: updated.phone || "",
          })
        );
      } catch {
        // ignore localStorage parse errors
      }

      setProfileFeedback({ type: "success", message: "Account details updated successfully." });
    } catch (error) {
      setProfileFeedback({
        type: "error",
        message: error?.message || "Failed to update account details.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const markPropertyFinished = async (propertyId, status) => {
    if (!currentAgentId || !propertyId) return;
    try {
      setFinishingPropertyId(propertyId);
      setAssignmentError("");
      await fetchApiJson(`/api/agents/${currentAgentId}/assigned-properties/${propertyId}/finish`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      await Promise.all([loadAssignedProperties(), loadPropertyHistory()]);
    } catch (error) {
      setAssignmentError(error?.message || "Failed to update property status.");
    } finally {
      setFinishingPropertyId("");
    }
  };

  return (
    <div>
      <div className="liton__wishlist-area pb-70">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              {/* PRODUCT TAB AREA START */}
              <div className="ltn__product-tab-area">
                <div className="container">
                  <div className="row">
                    <div className="col-lg-4">
                      <div className="ltn__tab-menu-list mb-50">
                        <div className="nav">
                          <a
                            className="show active"
                            data-bs-toggle="tab"
                            href="#ltn_tab_1_1"
                          >
                            Dashboard <i className="fas fa-home" />
                          </a>
                          <a data-bs-toggle="tab" href="#ltn_tab_1_2" className="">
                            Profiles <i className="fas fa-user" />
                          </a>
                          <a data-bs-toggle="tab" href="#ltn_tab_1_3">
                            address <i className="fas fa-map-marker-alt" />
                          </a>
                          <a data-bs-toggle="tab" href="#ltn_tab_1_4">
                            Account Details <i className="fas fa-user" />
                          </a>
                          <a data-bs-toggle="tab" href="#ltn_tab_1_5">
                            My Properties <i className="fa-solid fa-list" />
                          </a>
                          <a data-bs-toggle="tab" href="#ltn_tab_1_6">
                            History <i className="fa-solid fa-clock-rotate-left" />
                          </a>
                          {isGalleryManager ? (
                            <a data-bs-toggle="tab" href="#ltn_tab_1_10">
                              Gallery <i className="fas fa-images" />
                            </a>
                          ) : null}
                      
                          <a data-bs-toggle="tab" href="#ltn_tab_1_9">
                            Change Password <i className="fa-solid fa-lock" />
                          </a>
                          <button type="button" onClick={handleLogout} className="border-0 bg-transparent text-start p-0">
                            Logout <i className="fas fa-sign-out-alt" />
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-8">
                      <div className="tab-content">
                        <div className="tab-pane fade active show" id="ltn_tab_1_1">
                          <div className="ltn__myaccount-tab-content-inner">
                            <p>
                              Hello <strong>{displayName}</strong> (not{" "}
                              <strong>{displayName}</strong>?{" "}
                              <small>
                                <Link to="/login" onClick={handleLogout}>Log out</Link>
                              </small>{" "}
                              )
                            </p>
                            <p>
                              From your account dashboard you can view your{" "}
                              <span>recent orders</span>, manage your{" "}
                              <span>shipping and billing addresses</span>, and{" "}
                              <span>edit your password and account details</span>.
                            </p>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="ltn_tab_1_2">
                          <div className="ltn__myaccount-tab-content-inner">
                            {/* comment-area */}
                            <div className="ltn__comment-area mb-50">
                              <div className="ltn-author-introducing clearfix">
                                <div className="author-img">
                                  <img
                                    src={resolveProfileImage(profileForm.profileImageUrl)}
                                    alt={profileForm.fullName || "Agent"}
                                    onError={handleProfileImageFallback}
                                  />
                                </div>
                                <div className="author-info">
                                  <h6>{profileForm.companyName || "Agent of Property"}</h6>
                                  <h2>{profileForm.fullName || displayName}</h2>
                                  <div className="footer-address">
                                    <ul>
                                      <li>
                                        <div className="footer-address-icon">
                                          <i className="icon-placeholder" />
                                        </div>
                                        <div className="footer-address-info">
                                          <p>{profileForm.address || "Address not set"}</p>
                                        </div>
                                      </li>
                                      <li>
                                        <div className="footer-address-icon">
                                          <i className="icon-call" />
                                        </div>
                                        <div className="footer-address-info">
                                          <p>
                                            <a href={`tel:${profileForm.phone || ""}`}>
                                              {profileForm.phone || "Phone not set"}
                                            </a>
                                          </p>
                                        </div>
                                      </li>
                                      <li>
                                        <div className="footer-address-icon">
                                          <i className="icon-mail" />
                                        </div>
                                        <div className="footer-address-info">
                                          <p>
                                            <a href={`mailto:${profileForm.email || ""}`}>
                                              {profileForm.email || "Email not set"}
                                            </a>
                                          </p>
                                        </div>
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              <div className="ltn__form-box contact-form-box box-shadow white-bg">
                                <h4 className="title-2">Get A Quote</h4>
                                <form
                                  id="contact-form"
                                  action="https://tunatheme.com/tf/html/quarter-preview/quarter/mail.php"
                                  method="post"
                                >
                                  <div className="row">
                                    <div className="col-md-6">
                                      <div className="input-item input-item-name ltn__custom-icon">
                                        <input
                                          type="text"
                                          name="name"
                                          placeholder="Enter your name"
                                        />
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="input-item input-item-email ltn__custom-icon">
                                        <input
                                          type="email"
                                          name="email"
                                          placeholder="Enter email address"
                                        />
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="input-item">
                                        <select
                                          className="nice-select"
                                          style={{ display: "none" }}
                                        >
                                          <option>Select Service Type</option>
                                          <option>Property Management </option>
                                          <option>Mortgage Service </option>
                                          <option>Consulting Service</option>
                                          <option>Home Buying</option>
                                          <option>Home Selling</option>
                                          <option>Escrow Services</option>
                                        </select>
                                        <div className="nice-select" tabIndex={0}>
                                          <span className="current">
                                            Select Service Type
                                          </span>
                                          <ul className="list">
                                            <li
                                              data-value="Select Service Type"
                                              className="option selected"
                                            >
                                              Select Service Type
                                            </li>
                                            <li
                                              data-value="Property Management"
                                              className="option"
                                            >
                                              Property Management{" "}
                                            </li>
                                            <li
                                              data-value="Mortgage Service"
                                              className="option"
                                            >
                                              Mortgage Service{" "}
                                            </li>
                                            <li
                                              data-value="Consulting Service"
                                              className="option"
                                            >
                                              Consulting Service
                                            </li>
                                            <li
                                              data-value="Home Buying"
                                              className="option"
                                            >
                                              Home Buying
                                            </li>
                                            <li
                                              data-value="Home Selling"
                                              className="option"
                                            >
                                              Home Selling
                                            </li>
                                            <li
                                              data-value="Escrow Services"
                                              className="option"
                                            >
                                              Escrow Services
                                            </li>
                                          </ul>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="input-item input-item-phone ltn__custom-icon">
                                        <input
                                          type="text"
                                          name="phone"
                                          placeholder="Enter phone number"
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <div className="input-item input-item-textarea ltn__custom-icon">
                                    <textarea
                                      name="message"
                                      placeholder="Enter message"
                                      defaultValue={""}
                                    />
                                  </div>
                                  <p>
                                    <label className="input-info-save mb-0">
                                      <input type="checkbox" name="agree" /> Save my
                                      name, email, and website in this browser for the
                                      next time I comment.
                                    </label>
                                  </p>
                                  <div className="btn-wrapper mt-0">
                                    <button
                                      className="btn theme-btn-1 btn-effect-1 text-uppercase"
                                      type="submit"
                                    >
                                      get a free service
                                    </button>
                                  </div>
                                  <p className="form-messege mb-0 mt-20" />
                                </form>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="ltn_tab_1_3">
                          <div className="ltn__myaccount-tab-content-inner">
                            <p>
                              The following addresses will be used on the checkout
                              page by default.
                            </p>
                            <div className="row">
                              <div className="col-md-6 col-12 learts-mb-30">
                                <h4>
                                  Billing Address{" "}
                                  <small>
                                    <a href="#">edit</a>
                                  </small>
                                </h4>
                                <address>
                                  <p>
                                    <strong>Alex Tuntuni</strong>
                                  </p>
                                  <p>
                                    1355 Market St, Suite 900 <br />
                                    San Francisco, CA 94103
                                  </p>
                                  <p>Mobile: (123) 456-7890</p>
                                </address>
                              </div>
                              <div className="col-md-6 col-12 learts-mb-30">
                                <h4>
                                  Shipping Address{" "}
                                  <small>
                                    <a href="#">edit</a>
                                  </small>
                                </h4>
                                <address>
                                  <p>
                                    <strong>Alex Tuntuni</strong>
                                  </p>
                                  <p>
                                    1355 Market St, Suite 900 <br />
                                    San Francisco, CA 94103
                                  </p>
                                  <p>Mobile: (123) 456-7890</p>
                                </address>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="ltn_tab_1_4">
                          <div className="ltn__myaccount-tab-content-inner">
                            <p>Manage your account details and profile image.</p>
                            <div className="ltn__form-box">
                              {profileLoading ? <Loader label="Loading account details..." /> : null}
                              {!profileLoading ? (
                                <form onSubmit={handleProfileSubmit}>
                                <div className="row mb-30">
                                  <div className="col-12 mb-3 d-flex align-items-center gap-3">
                                    <img
                                      src={resolveProfileImage(profileForm.profileImageUrl)}
                                      alt={profileForm.fullName || "Agent"}
                                      onError={handleProfileImageFallback}
                                      style={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover" }}
                                    />
                                    <div>
                                      <label className="mb-2 d-block">Profile image:</label>
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleProfileImageUpload}
                                        disabled={profileUploading}
                                      />
                                      <small className="d-block mt-1">
                                        {profileUploading ? "Uploading image..." : "Upload from account details."}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                <div className="row mb-50">
                                  <div className="col-md-6">
                                    <label>Full name:</label>
                                    <input
                                      type="text"
                                      name="fullName"
                                      value={profileForm.fullName}
                                      onChange={handleProfileChange}
                                      required
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label>Email:</label>
                                    <input
                                      type="email"
                                      name="email"
                                      value={profileForm.email}
                                      onChange={handleProfileChange}
                                      required
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label>Phone:</label>
                                    <input
                                      type="text"
                                      name="phone"
                                      value={profileForm.phone}
                                      onChange={handleProfileChange}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label>Company name:</label>
                                    <input
                                      type="text"
                                      name="companyName"
                                      value={profileForm.companyName}
                                      onChange={handleProfileChange}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label>License number:</label>
                                    <input
                                      type="text"
                                      name="licenseNumber"
                                      value={profileForm.licenseNumber}
                                      onChange={handleProfileChange}
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label>Address:</label>
                                    <input
                                      type="text"
                                      name="address"
                                      value={profileForm.address}
                                      onChange={handleProfileChange}
                                    />
                                  </div>
                                  <div className="col-md-12">
                                    <label>Bio:</label>
                                    <textarea
                                      name="bio"
                                      value={profileForm.bio}
                                      onChange={handleProfileChange}
                                      rows={4}
                                      placeholder="Tell clients about your profile..."
                                    />
                                  </div>
                                </div>
                                <div className="btn-wrapper">
                                  <button
                                    type="submit"
                                    className="btn theme-btn-1 btn-effect-1 text-uppercase"
                                    disabled={profileSaving || profileUploading}
                                  >
                                    {profileSaving ? "Saving..." : "Save Changes"}
                                  </button>
                                </div>
                                {profileFeedback.message ? (
                                  <p className={`mt-3 mb-0 ${profileFeedback.type === "error" ? "text-danger" : "text-success"}`}>
                                    {profileFeedback.message}
                                  </p>
                                ) : null}
                              </form>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="ltn_tab_1_5">
                          <div className="ltn__myaccount-tab-content-inner">
                            {assignmentError ? <p className="text-danger">{assignmentError}</p> : null}
                            {loadingAssigned ? <Loader label="Loading assigned properties..." /> : null}
                            {!loadingAssigned && assignedProperties.length === 0 ? (
                              <p>No assigned properties found.</p>
                            ) : null}
                            {!loadingAssigned && assignedProperties.length > 0 ? (
                              <div className="ltn__my-properties-table table-responsive">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th scope="col">My Properties</th>
                                      <th scope="col">Status</th>
                                      <th scope="col">Assigned</th>
                                      <th scope="col">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {assignedProperties.map((item) => (
                                      <tr key={item.assignmentId}>
                                        <td>
                                          <div className="d-flex align-items-center gap-3">
                                            <img
                                              src={resolvePropertyImage(item.property?.imageUrl)}
                                              alt={item.property?.title || "Property"}
                                              onError={handlePropertyImageFallback}
                                              style={{ width: 90, height: 70, objectFit: "cover", borderRadius: 6 }}
                                            />
                                            <div>
                                              <h6 className="mb-1">{item.property?.title || "Untitled Property"}</h6>
                                              <small>
                                                <i className="icon-placeholder" /> {item.property?.city || "—"}, {item.property?.area || "—"}
                                              </small>
                                            </div>
                                          </div>
                                        </td>
                                        <td>
                                          <span className="badge bg-secondary">{item.property?.status || "ACTIVE"}</span>
                                        </td>
                                        <td>{item.assignedAt ? new Date(item.assignedAt).toLocaleString() : "—"}</td>
                                        <td>
                                          <details style={{ position: "relative", display: "inline-block" }}>
                                            <summary
                                              style={{
                                                listStyle: "none",
                                                cursor: finishingPropertyId === item.property?.id ? "not-allowed" : "pointer",
                                                width: 36,
                                                height: 36,
                                                borderRadius: 8,
                                                border: "1px solid #e5e7eb",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: "#fff",
                                                color: "#333",
                                                opacity: finishingPropertyId === item.property?.id ? 0.65 : 1,
                                              }}
                                            >
                                              <i className="fa-solid fa-pen-to-square" />
                                            </summary>
                                            <div
                                              style={{
                                                position: "absolute",
                                                top: 42,
                                                right: 0,
                                                minWidth: 150,
                                                background: "#fff",
                                                border: "1px solid #ececec",
                                                borderRadius: 8,
                                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                                zIndex: 10,
                                                padding: 6,
                                              }}
                                            >
                                              <button
                                                type="button"
                                                disabled={finishingPropertyId === item.property?.id}
                                                onClick={() => markPropertyFinished(item.property?.id, "SOLD")}
                                                style={{
                                                  width: "100%",
                                                  border: 0,
                                                  background: "transparent",
                                                  textAlign: "left",
                                                  padding: "8px 10px",
                                                  borderRadius: 6,
                                                  color: "#222",
                                                }}
                                              >
                                                <i className="fa-solid fa-circle-check me-2" />
                                                Sold
                                              </button>
                                              <button
                                                type="button"
                                                disabled={finishingPropertyId === item.property?.id}
                                                onClick={() => markPropertyFinished(item.property?.id, "RENTED")}
                                                style={{
                                                  width: "100%",
                                                  border: 0,
                                                  background: "transparent",
                                                  textAlign: "left",
                                                  padding: "8px 10px",
                                                  borderRadius: 6,
                                                  color: "#222",
                                                }}
                                              >
                                                <i className="fa-solid fa-key me-2" />
                                                Rented
                                              </button>
                                            </div>
                                          </details>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="tab-pane fade" id="ltn_tab_1_6">
                          <div className="ltn__myaccount-tab-content-inner">
                            {loadingHistory ? <Loader label="Loading property history..." /> : null}
                            {!loadingHistory && propertyHistory.length === 0 ? (
                              <p>No history records yet.</p>
                            ) : null}
                            {!loadingHistory && propertyHistory.length > 0 ? (
                              <div className="ltn__my-properties-table table-responsive">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th scope="col">Property</th>
                                      <th scope="col">Old Status</th>
                                      <th scope="col">New Status</th>
                                      <th scope="col">Changed At</th>
                                      <th scope="col">Note</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {propertyHistory.map((row) => (
                                      <tr key={row.id}>
                                        <td>
                                          <div className="d-flex align-items-center gap-3">
                                            <img
                                              src={resolvePropertyImage(row.imageUrl)}
                                              alt={row.propertyTitle || "Property"}
                                              onError={handlePropertyImageFallback}
                                              style={{ width: 90, height: 70, objectFit: "cover", borderRadius: 6 }}
                                            />
                                            <div>{row.propertyTitle || "Untitled Property"}</div>
                                          </div>
                                        </td>
                                        <td>{row.oldStatus || "—"}</td>
                                        <td>{row.newStatus || "—"}</td>
                                        <td>{row.changedAt ? new Date(row.changedAt).toLocaleString() : "—"}</td>
                                        <td>{row.note || "—"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <div className="tab-pane fade" id="ltn_tab_1_7">
                          <div className="ltn__myaccount-tab-content-inner">
                            <h6>Property Description</h6>
                            <div className="row">
                              <div className="col-md-12">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="*Title (mandatory)"
                                  />
                                </div>
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <textarea
                                    name="ltn__message"
                                    placeholder="Description"
                                    defaultValue={""}
                                  />
                                </div>
                              </div>
                            </div>
                            <h6>Property Price</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="input-item  input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Price in $ (only numbers)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="After Price Label (ex: /month)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Before Price Label (ex: from)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Yearly Tax Rate"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Homeowners Association Fee(monthly)"
                                  />
                                </div>
                              </div>
                            </div>
                            <h6>Select Categories</h6>
                            <div className="row">
                              <div className="col-lg-4 col-md-6">
                                <div className="input-item">
                                  <select
                                    className="nice-select"
                                    style={{ display: "none" }}
                                  >
                                    <option>None</option>
                                    <option>Apartments</option>
                                    <option>Condos</option>
                                    <option>Duplexes</option>
                                    <option>Houses</option>
                                    <option>Industrial</option>
                                    <option>Land</option>
                                    <option>Offices</option>
                                    <option>Retail</option>
                                    <option>Villas</option>
                                  </select>
                                  <div className="nice-select" tabIndex={0}>
                                    <span className="current">None</span>
                                    <ul className="list">
                                      <li
                                        data-value="None"
                                        className="option selected"
                                      >
                                        None
                                      </li>
                                      <li data-value="Apartments" className="option">
                                        Apartments
                                      </li>
                                      <li data-value="Condos" className="option">
                                        Condos
                                      </li>
                                      <li data-value="Duplexes" className="option">
                                        Duplexes
                                      </li>
                                      <li data-value="Houses" className="option">
                                        Houses
                                      </li>
                                      <li data-value="Industrial" className="option">
                                        Industrial
                                      </li>
                                      <li data-value="Land" className="option">
                                        Land
                                      </li>
                                      <li data-value="Offices" className="option">
                                        Offices
                                      </li>
                                      <li data-value="Retail" className="option">
                                        Retail
                                      </li>
                                      <li data-value="Villas" className="option">
                                        Villas
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <div className="input-item">
                                  <select
                                    className="nice-select"
                                    style={{ display: "none" }}
                                  >
                                    <option>None</option>
                                    <option>Rentals</option>
                                    <option>Sales</option>
                                  </select>
                                  <div className="nice-select" tabIndex={0}>
                                    <span className="current">None</span>
                                    <ul className="list">
                                      <li
                                        data-value="None"
                                        className="option selected"
                                      >
                                        None
                                      </li>
                                      <li data-value="Rentals" className="option">
                                        Rentals
                                      </li>
                                      <li data-value="Sales" className="option">
                                        Sales
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <div className="input-item">
                                  <select
                                    className="nice-select"
                                    style={{ display: "none" }}
                                  >
                                    <option>no status</option>
                                    <option>Active</option>
                                    <option>hot offer</option>
                                    <option>new offer</option>
                                    <option>open house</option>
                                    <option>sold</option>
                                  </select>
                                  <div className="nice-select" tabIndex={0}>
                                    <span className="current">no status</span>
                                    <ul className="list">
                                      <li
                                        data-value="no status"
                                        className="option selected"
                                      >
                                        no status
                                      </li>
                                      <li data-value="Active" className="option">
                                        Active
                                      </li>
                                      <li data-value="hot offer" className="option">
                                        hot offer
                                      </li>
                                      <li data-value="new offer" className="option">
                                        new offer
                                      </li>
                                      <li data-value="open house" className="option">
                                        open house
                                      </li>
                                      <li data-value="sold" className="option">
                                        sold
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <h6>Listing Media</h6>
                            <input
                              type="file"
                              id="myFile"
                              name="filename"
                              className="btn theme-btn-3 mb-10"
                              accept="image/*"
                              multiple
                            />
                            <br />
                            <p>
                              <small>
                                * At least 1 image is required for a valid
                                submission.Minimum size is 500/500px.
                              </small>
                              <br />
                              <small>* PDF files upload supported as well.</small>
                              <br />
                              <small>
                                * Images might take longer to be processed.
                              </small>
                              <br />
                              <small>* You can select multiple property images in one upload.</small>
                            </p>
                            <h6>Video Option</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="input-item">
                                  <select
                                    className="nice-select"
                                    style={{ display: "none" }}
                                  >
                                    <option>Video from</option>
                                    <option>vimeo</option>
                                    <option>youtube</option>
                                  </select>
                                  <div className="nice-select" tabIndex={0}>
                                    <span className="current">Video from</span>
                                    <ul className="list">
                                      <li
                                        data-value="Video from"
                                        className="option selected"
                                      >
                                        Video from
                                      </li>
                                      <li data-value="vimeo" className="option">
                                        vimeo
                                      </li>
                                      <li data-value="youtube" className="option">
                                        youtube
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Embed Video ID"
                                  />
                                </div>
                              </div>
                            </div>
                            <h6>Virtual Tour</h6>
                            <div className="input-item input-item-textarea ltn__custom-icon">
                              <textarea
                                name="ltn__message"
                                placeholder="Virtual Tour:"
                                defaultValue={""}
                              />
                            </div>
                            <h6>Listing Location</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="*Address"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Country"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="County / State"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="City"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Neighborhood"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Zip"
                                  />
                                </div>
                              </div>
                              <div className="col-lg-12">
                                <div className="property-details-google-map mb-60">
                                  <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d9334.271551495209!2d-73.97198251485975!3d40.668170674982946!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c25b0456b5a2e7%3A0x68bdf865dda0b669!2sBrooklyn%20Botanic%20Garden%20Shop!5e0!3m2!1sen!2sbd!4v1590597267201!5m2!1sen!2sbd"
                                    width="100%"
                                    height="100%"
                                    frameBorder={0}
                                    allowFullScreen=""
                                    aria-hidden="false"
                                    tabIndex={0}
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Latitude (for Google Maps)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Longitude (for Google Maps)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <label className="checkbox-item">
                                  Enable Google Street View
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Google Street View - Camera Angle (value from 0 to 360)"
                                  />
                                </div>
                              </div>
                            </div>
                            <h6>Listing Details</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Size in ft2 (*only numbers)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Lot Size in ft2 (*only numbers)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Rooms (*only numbers)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Bedrooms (*only numbers)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Bathrooms (*only numbers)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Custom ID (*text)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Garages (*text)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Year Built (*numeric)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Garage Size (*text)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Available from (*date)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Basement (*text)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Extra Details (*text)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Roofing (*text)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Exterior Material (*text)"
                                  />
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item">
                                  <select
                                    className="nice-select"
                                    style={{ display: "none" }}
                                  >
                                    <option>Structure Type</option>
                                    <option>Not Available</option>
                                    <option>Brick</option>
                                    <option>Wood</option>
                                    <option>Cement</option>
                                  </select>
                                  <div className="nice-select" tabIndex={0}>
                                    <span className="current">Structure Type</span>
                                    <ul className="list">
                                      <li
                                        data-value="Structure Type"
                                        className="option selected"
                                      >
                                        Structure Type
                                      </li>
                                      <li
                                        data-value="Not Available"
                                        className="option"
                                      >
                                        Not Available
                                      </li>
                                      <li data-value="Brick" className="option">
                                        Brick
                                      </li>
                                      <li data-value="Wood" className="option">
                                        Wood
                                      </li>
                                      <li data-value="Cement" className="option">
                                        Cement
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item">
                                  <select
                                    className="nice-select"
                                    style={{ display: "none" }}
                                  >
                                    <option>Floors No</option>
                                    <option>Not Available</option>
                                    <option>1</option>
                                    <option>2</option>
                                    <option>3</option>
                                    <option>4</option>
                                    <option>5</option>
                                  </select>
                                  <div className="nice-select" tabIndex={0}>
                                    <span className="current">Floors No</span>
                                    <ul className="list">
                                      <li
                                        data-value="Floors No"
                                        className="option selected"
                                      >
                                        Floors No
                                      </li>
                                      <li
                                        data-value="Not Available"
                                        className="option"
                                      >
                                        Not Available
                                      </li>
                                      <li data-value={1} className="option">
                                        1
                                      </li>
                                      <li data-value={2} className="option">
                                        2
                                      </li>
                                      <li data-value={3} className="option">
                                        3
                                      </li>
                                      <li data-value={4} className="option">
                                        4
                                      </li>
                                      <li data-value={5} className="option">
                                        5
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-12">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <textarea
                                    name="ltn__message"
                                    placeholder="Owner/Agent notes (*not visible on front end)"
                                    defaultValue={""}
                                  />
                                </div>
                              </div>
                            </div>
                            <h6>Select Energy Class</h6>
                            <div className="row">
                              <div className="col-md-6">
                                <div className="input-item">
                                  <select
                                    className="nice-select"
                                    style={{ display: "none" }}
                                  >
                                    <option>
                                      Select Energy Class (EU regulation)
                                    </option>
                                    <option>A+</option>
                                    <option>A</option>
                                    <option>B</option>
                                    <option>C</option>
                                    <option>D</option>
                                    <option>E</option>
                                  </select>
                                  <div className="nice-select" tabIndex={0}>
                                    <span className="current">
                                      Select Energy Class (EU regulation)
                                    </span>
                                    <ul className="list">
                                      <li
                                        data-value="Select Energy Class (EU regulation)"
                                        className="option selected"
                                      >
                                        Select Energy Class (EU regulation)
                                      </li>
                                      <li data-value="A+" className="option">
                                        A+
                                      </li>
                                      <li data-value="A" className="option">
                                        A
                                      </li>
                                      <li data-value="B" className="option">
                                        B
                                      </li>
                                      <li data-value="C" className="option">
                                        C
                                      </li>
                                      <li data-value="D" className="option">
                                        D
                                      </li>
                                      <li data-value="E" className="option">
                                        E
                                      </li>
                                    </ul>
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="input-item input-item-textarea ltn__custom-icon">
                                  <input
                                    type="text"
                                    name="ltn__name"
                                    placeholder="Energy Index in kWh/m2a"
                                  />
                                </div>
                              </div>
                            </div>
                            <h6>Amenities and Features</h6>
                            <h6>Interior Details</h6>
                            <div className="row">
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Equipped Kitchen
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Gym
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Laundry
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Media Room
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                            </div>
                            <h6 className="mt-20">Outdoor Details</h6>
                            <div className="row">
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Back yard
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Basketball court
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Front yard
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Garage Attached
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Hot Bath
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Pool
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                            </div>
                            <h6 className="mt-20">Utilities</h6>
                            <div className="row">
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Central Air
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Electricity
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Heating
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Natural Gas
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Ventilation
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Water
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                            </div>
                            <h6 className="mt-20">Other Features</h6>
                            <div className="row">
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Chair Accessible
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Elevator
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Fireplace
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Smoke detectors
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  Washer and dryer
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                              <div className="col-lg-4 col-md-6">
                                <label className="checkbox-item">
                                  WiFi
                                  <input type="checkbox" />
                                  <span className="checkmark" />
                                </label>
                              </div>
                            </div>
                            <div className="alert alert-warning d-none" role="alert">
                              Please note that the date and time you requested may not
                              be available. We will contact you to confirm your actual
                              appointment details.
                            </div>
                            <div className="btn-wrapper text-center--- mt-30">
                              <button
                                className="btn theme-btn-1 btn-effect-1 text-uppercase"
                                type="submit"
                              >
                                Submit Property
                              </button>
                            </div>
                          </div>
                        </div>
                        <div className="tab-pane fade" id="ltn_tab_1_8">
                          <div className="ltn__myaccount-tab-content-inner">
                            <div className="row">
                              <div className="col-lg-12">
                                <div className="ltn__checkout-inner">
                                  <div className="ltn__checkout-single-content ltn__returning-customer-wrap">
                                    <h5>
                                      Returning customer?{" "}
                                      <a
                                        className="ltn__secondary-color"
                                        href="#ltn__returning-customer-login"
                                        data-toggle="collapse"
                                      >
                                        Click here to login
                                      </a>
                                    </h5>
                                    <div
                                      id="ltn__returning-customer-login"
                                      className="collapse ltn__checkout-single-content-info"
                                    >
                                      <div className="ltn_coupon-code-form ltn__form-box">
                                        <p>Please login your accont.</p>
                                        <form action="#">
                                          <div className="row">
                                            <div className="col-md-6">
                                              <div className="input-item input-item-name ltn__custom-icon">
                                                <input
                                                  type="text"
                                                  name="ltn__name"
                                                  placeholder="Enter your name"
                                                />
                                              </div>
                                            </div>
                                            <div className="col-md-6">
                                              <div className="input-item input-item-email ltn__custom-icon">
                                                <input
                                                  type="email"
                                                  name="ltn__email"
                                                  placeholder="Enter email address"
                                                />
                                              </div>
                                            </div>
                                          </div>
                                          <button className="btn theme-btn-1 btn-effect-1 text-uppercase">
                                            Login
                                          </button>
                                          <label className="input-info-save mb-0">
                                            <input type="checkbox" name="agree" />{" "}
                                            Remember me
                                          </label>
                                          <p className="mt-30">
                                            <a href="register.html">
                                              Lost your password?
                                            </a>
                                          </p>
                                        </form>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="ltn__checkout-single-content ltn__coupon-code-wrap">
                                    <h5>
                                      Have a coupon?{" "}
                                      <a
                                        className="ltn__secondary-color"
                                        href="#ltn__coupon-code"
                                        data-toggle="collapse"
                                      >
                                        Click here to enter your code
                                      </a>
                                    </h5>
                                    <div
                                      id="ltn__coupon-code"
                                      className="collapse ltn__checkout-single-content-info"
                                    >
                                      <div className="ltn__coupon-code-form">
                                        <p>
                                          If you have a coupon code, please apply it
                                          below.
                                        </p>
                                        <form action="#">
                                          <input
                                            type="text"
                                            name="coupon-code"
                                            placeholder="Coupon code"
                                          />
                                          <button className="btn theme-btn-2 btn-effect-2 text-uppercase">
                                            Apply Coupon
                                          </button>
                                        </form>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="ltn__checkout-single-content mt-50">
                                    <h4 className="title-2">Billing Details</h4>
                                    <div className="ltn__checkout-single-content-info">
                                      <form action="#">
                                        <h6>Personal Information</h6>
                                        <div className="row">
                                          <div className="col-md-6">
                                            <div className="input-item input-item-name ltn__custom-icon">
                                              <input
                                                type="text"
                                                name="ltn__name"
                                                placeholder="First name"
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="input-item input-item-name ltn__custom-icon">
                                              <input
                                                type="text"
                                                name="ltn__lastname"
                                                placeholder="Last name"
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="input-item input-item-email ltn__custom-icon">
                                              <input
                                                type="email"
                                                name="ltn__email"
                                                placeholder="email address"
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="input-item input-item-phone ltn__custom-icon">
                                              <input
                                                type="text"
                                                name="ltn__phone"
                                                placeholder="phone number"
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="input-item input-item-website ltn__custom-icon">
                                              <input
                                                type="text"
                                                name="ltn__company"
                                                placeholder="Company name (optional)"
                                              />
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="input-item input-item-website ltn__custom-icon">
                                              <input
                                                type="text"
                                                name="ltn__phone"
                                                placeholder="Company address (optional)"
                                              />
                                            </div>
                                          </div>
                                        </div>
                                        <div className="row">
                                          <div className="col-lg-4 col-md-6">
                                            <h6>Country</h6>
                                            <div className="input-item">
                                              <select
                                                className="nice-select"
                                                style={{ display: "none" }}
                                              >
                                                <option>Select Country</option>
                                                <option>Australia</option>
                                                <option>Canada</option>
                                                <option>China</option>
                                                <option>Morocco</option>
                                                <option>Saudi Arabia</option>
                                                <option>United Kingdom (UK)</option>
                                                <option>United States (US)</option>
                                              </select>
                                              <div
                                                className="nice-select"
                                                tabIndex={0}
                                              >
                                                <span className="current">
                                                  Select Country
                                                </span>
                                                <ul className="list">
                                                  <li
                                                    data-value="Select Country"
                                                    className="option selected"
                                                  >
                                                    Select Country
                                                  </li>
                                                  <li
                                                    data-value="Australia"
                                                    className="option"
                                                  >
                                                    Australia
                                                  </li>
                                                  <li
                                                    data-value="Canada"
                                                    className="option"
                                                  >
                                                    Canada
                                                  </li>
                                                  <li
                                                    data-value="China"
                                                    className="option"
                                                  >
                                                    China
                                                  </li>
                                                  <li
                                                    data-value="Morocco"
                                                    className="option"
                                                  >
                                                    Morocco
                                                  </li>
                                                  <li
                                                    data-value="Saudi Arabia"
                                                    className="option"
                                                  >
                                                    Saudi Arabia
                                                  </li>
                                                  <li
                                                    data-value="United Kingdom (UK)"
                                                    className="option"
                                                  >
                                                    United Kingdom (UK)
                                                  </li>
                                                  <li
                                                    data-value="United States (US)"
                                                    className="option"
                                                  >
                                                    United States (US)
                                                  </li>
                                                </ul>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-lg-12 col-md-12">
                                            <h6>Address</h6>
                                            <div className="row">
                                              <div className="col-md-6">
                                                <div className="input-item">
                                                  <input
                                                    type="text"
                                                    placeholder="House number and street name"
                                                  />
                                                </div>
                                              </div>
                                              <div className="col-md-6">
                                                <div className="input-item">
                                                  <input
                                                    type="text"
                                                    placeholder="Apartment, suite, unit etc. (optional)"
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="col-lg-4 col-md-6">
                                            <h6>Town / City</h6>
                                            <div className="input-item">
                                              <input type="text" placeholder="City" />
                                            </div>
                                          </div>
                                          <div className="col-lg-4 col-md-6">
                                            <h6>State </h6>
                                            <div className="input-item">
                                              <input
                                                type="text"
                                                placeholder="State"
                                              />
                                            </div>
                                          </div>
                                          <div className="col-lg-4 col-md-6">
                                            <h6>Zip</h6>
                                            <div className="input-item">
                                              <input type="text" placeholder="Zip" />
                                            </div>
                                          </div>
                                        </div>
                                        <p>
                                          <label className="input-info-save mb-0">
                                            <input type="checkbox" name="agree" />{" "}
                                            Create an account?
                                          </label>
                                        </p>
                                        <h6>Order Notes (optional)</h6>
                                        <div className="input-item input-item-textarea ltn__custom-icon">
                                          <textarea
                                            name="ltn__message"
                                            placeholder="Notes about your order, e.g. special notes for delivery."
                                            defaultValue={""}
                                          />
                                        </div>
                                      </form>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-6">
                                <div className="ltn__checkout-payment-method mt-50">
                                  <h4 className="title-2">Payment Method</h4>
                                  <div id="checkout_accordion_1">
                                    {/* card */}
                                    <div className="card">
                                      <h5
                                        className="collapsed ltn__card-title"
                                        data-toggle="collapse"
                                        data-target="#faq-item-2-1"
                                        aria-expanded="false"
                                      >
                                        Check payments
                                      </h5>
                                      <div
                                        id="faq-item-2-1"
                                        className="collapse"
                                        data-parent="#checkout_accordion_1"
                                      >
                                        <div className="card-body">
                                          <p>
                                            Please send a check to Store Name, Store
                                            Street, Store Town, Store State / County,
                                            Store Postcode.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                    {/* card */}
                                    <div className="card">
                                      <h5
                                        className="ltn__card-title"
                                        data-toggle="collapse"
                                        data-target="#faq-item-2-2"
                                        aria-expanded="true"
                                      >
                                        Cash on delivery
                                      </h5>
                                      <div
                                        id="faq-item-2-2"
                                        className="collapse show"
                                        data-parent="#checkout_accordion_1"
                                      >
                                        <div className="card-body">
                                          <p>Pay with cash upon delivery.</p>
                                        </div>
                                      </div>
                                    </div>
                                    {/* card */}
                                    <div className="card">
                                      <h5
                                        className="collapsed ltn__card-title"
                                        data-toggle="collapse"
                                        data-target="#faq-item-2-3"
                                        aria-expanded="false"
                                      >
                                        PayPal{" "}
                                        <img src="img/icons/payment-3.png" alt="#" />
                                      </h5>
                                      <div
                                        id="faq-item-2-3"
                                        className="collapse"
                                        data-parent="#checkout_accordion_1"
                                      >
                                        <div className="card-body">
                                          <p>
                                            Pay via PayPal; you can pay with your
                                            credit card if you don’t have a PayPal
                                            account.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="ltn__payment-note mt-30 mb-30">
                                    <p>
                                      Your personal data will be used to process your
                                      order, support your experience throughout this
                                      website, and for other purposes described in our
                                      privacy policy.
                                    </p>
                                  </div>
                                  <button
                                    className="btn theme-btn-1 btn-effect-1 text-uppercase"
                                    type="submit"
                                  >
                                    Place order
                                  </button>
                                </div>
                              </div>
                              <div className="col-lg-6">
                                <div className="shoping-cart-total mt-50">
                                  <h4 className="title-2">Cart Totals</h4>
                                  <table className="table">
                                    <tbody>
                                      <tr>
                                        <td>
                                          3 Rooms Manhattan <strong>× 2</strong>
                                        </td>
                                        <td>$298.00</td>
                                      </tr>
                                      <tr>
                                        <td>
                                          OE Replica Wheels <strong>× 2</strong>
                                        </td>
                                        <td>$170.00</td>
                                      </tr>
                                      <tr>
                                        <td>
                                          Wheel Bearing Retainer <strong>× 2</strong>
                                        </td>
                                        <td>$150.00</td>
                                      </tr>
                                      <tr>
                                        <td>Shipping and Handing</td>
                                        <td>$15.00</td>
                                      </tr>
                                      <tr>
                                        <td>Vat</td>
                                        <td>$00.00</td>
                                      </tr>
                                      <tr>
                                        <td>
                                          <strong>Order Total</strong>
                                        </td>
                                        <td>
                                          <strong>$633.00</strong>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {isGalleryManager ? (
                          <div className="tab-pane fade" id="ltn_tab_1_10">
                            <div className="ltn__myaccount-tab-content-inner">
                              <h4 className="title-2">{editingGalleryId ? "Edit Gallery Item" : "Gallery Uploads"}</h4>
                              <p>
                                {editingGalleryId
                                  ? "Update this gallery card and choose the primary image shown first on the public gallery page."
                                  : "Add public gallery cards. Choose a primary image for the card, and the search icon opens the full image set."}
                              </p>
                              <form onSubmit={handleGallerySubmit} className="ltn__form-box contact-form-box mb-50">
                                <div className="row">
                                  <div className="col-md-6">
                                    <label>Title:</label>
                                    <input
                                      type="text"
                                      name="title"
                                      value={galleryForm.title}
                                      onChange={handleGalleryChange}
                                      placeholder="Gallery title"
                                      required
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label>Category:</label>
                                    <select
                                      name="category"
                                      className="qtech-native-select"
                                      value={galleryForm.category}
                                      onChange={handleGalleryChange}
                                    >
                                      <option value="Property">Property</option>
                                      <option value="Team">Team</option>
                                      <option value="Event">Event</option>
                                    </select>
                                  </div>
                                  <div className="col-md-12">
                                    <label>Small description:</label>
                                    <textarea
                                      name="description"
                                      value={galleryForm.description}
                                      onChange={handleGalleryChange}
                                      rows={3}
                                      placeholder="Short description shown under the title"
                                    />
                                  </div>
                                  <div className="col-md-6">
                                    <label>Sort order:</label>
                                    <input
                                      type="number"
                                      name="sortOrder"
                                      value={galleryForm.sortOrder}
                                      onChange={handleGalleryChange}
                                    />
                                  </div>
                                  <div className="col-md-6 d-flex align-items-center">
                                    <label className="checkbox-item mb-0">
                                      Published
                                      <input
                                        type="checkbox"
                                        name="isPublished"
                                        checked={galleryForm.isPublished}
                                        onChange={handleGalleryChange}
                                      />
                                      <span className="checkmark" />
                                    </label>
                                  </div>
                                  <div className="col-md-12">
                                    <label>Gallery images:</label>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      multiple
                                      onChange={handleGalleryImageUpload}
                                      disabled={galleryUploading}
                                      className="btn theme-btn-3 mb-10"
                                    />
                                    <small className="d-block mb-3">
                                      {galleryUploading ? "Uploading images..." : "Upload one or more images. Mark one image as Primary for the gallery card."}
                                    </small>
                                    {galleryForm.imageUrls.length > 0 ? (
                                      <div className="row">
                                        {galleryForm.imageUrls.map((url, index) => (
                                          <div className="col-md-3 col-6 mb-3" key={`${url}-${index}`}>
                                            <div style={{ position: "relative" }}>
                                              {galleryForm.coverImageUrl === url ? (
                                                <span
                                                  style={{
                                                    position: "absolute",
                                                    top: 6,
                                                    left: 6,
                                                    zIndex: 2,
                                                    borderRadius: 999,
                                                    padding: "4px 9px",
                                                    background: "#bba55c",
                                                    color: "#fff",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                  }}
                                                >
                                                  Primary
                                                </span>
                                              ) : null}
                                              <img
                                                src={resolvePropertyImage(url)}
                                                alt={`Gallery upload ${index + 1}`}
                                                style={{ width: "100%", height: 95, objectFit: "cover", borderRadius: 8 }}
                                              />
                                              <button
                                                type="button"
                                                onClick={() => removeGalleryImage(index)}
                                                style={{
                                                  position: "absolute",
                                                  top: 6,
                                                  right: 6,
                                                  border: 0,
                                                  borderRadius: 999,
                                                  width: 28,
                                                  height: 28,
                                                  background: "#fff",
                                                  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                                                }}
                                                aria-label="Remove gallery image"
                                              >
                                                x
                                              </button>
                                            </div>
                                            <button
                                              type="button"
                                              className={`btn btn-sm mt-2 w-100 ${galleryForm.coverImageUrl === url ? "theme-btn-1" : "theme-btn-2"}`}
                                              onClick={() => setPrimaryGalleryImage(url)}
                                            >
                                              {galleryForm.coverImageUrl === url ? "Primary Image" : "Make Primary"}
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                <div className="btn-wrapper mt-20">
                                  <button
                                    type="submit"
                                    className="btn theme-btn-1 btn-effect-1 text-uppercase"
                                    disabled={gallerySaving || galleryUploading || galleryForm.imageUrls.length === 0}
                                  >
                                    {gallerySaving ? "Saving..." : editingGalleryId ? "Update Gallery Item" : "Publish Gallery Item"}
                                  </button>
                                  {editingGalleryId ? (
                                    <button
                                      type="button"
                                      className="btn theme-btn-2 btn-effect-2 text-uppercase ms-2"
                                      disabled={gallerySaving || galleryUploading}
                                      onClick={resetGalleryForm}
                                    >
                                      Cancel Edit
                                    </button>
                                  ) : null}
                                </div>
                                {galleryFeedback.message ? (
                                  <p className={`mt-3 mb-0 ${galleryFeedback.type === "error" ? "text-danger" : "text-success"}`}>
                                    {galleryFeedback.message}
                                  </p>
                                ) : null}
                              </form>

                              <h5 className="mb-20">Current Gallery Items</h5>
                              {galleryLoading ? <Loader label="Loading gallery items..." /> : null}
                              {!galleryLoading && galleryItems.length === 0 ? <p>No gallery items yet.</p> : null}
                              {!galleryLoading && galleryItems.length > 0 ? (
                                <div className="ltn__my-properties-table table-responsive">
                                  <table className="table">
                                    <thead>
                                      <tr>
                                        <th scope="col">Image</th>
                                        <th scope="col">Title</th>
                                        <th scope="col">Category</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {galleryItems.map((item) => (
                                        <tr key={item.id}>
                                          <td>
                                            <img
                                              src={resolvePropertyImage(item.coverImageUrl)}
                                              alt={item.title || "Gallery item"}
                                              style={{ width: 90, height: 70, objectFit: "cover", borderRadius: 6 }}
                                            />
                                          </td>
                                          <td>
                                            <strong>{item.title}</strong>
                                            <br />
                                            <small>{item.description || "—"}</small>
                                          </td>
                                          <td>{item.category || "Property"}</td>
                                          <td>{item.isPublished ? "Published" : "Draft"}</td>
                                          <td>
                                            <button
                                              type="button"
                                              className="btn btn-sm theme-btn-3 me-2"
                                              onClick={() => startEditGalleryItem(item)}
                                            >
                                              Edit
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm theme-btn-1 me-2"
                                              onClick={() => toggleGalleryPublish(item)}
                                            >
                                              {item.isPublished ? "Unpublish" : "Publish"}
                                            </button>
                                            <button
                                              type="button"
                                              className="btn btn-sm theme-btn-2"
                                              onClick={() => deleteGalleryItem(item.id)}
                                            >
                                              Delete
                                            </button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                        <div className="tab-pane fade" id="ltn_tab_1_9">
                          <div className="ltn__myaccount-tab-content-inner">
                            <div className="account-login-inner">
                              <form
                                action="#"
                                className="ltn__form-box contact-form-box"
                              >
                                <h5 className="mb-30">Change Password</h5>
                                <input
                                  type="password"
                                  name="password"
                                  placeholder="Current Password*"
                                />
                                <input
                                  type="password"
                                  name="password"
                                  placeholder="New Password*"
                                />
                                <input
                                  type="password"
                                  name="password"
                                  placeholder="Confirm New Password*"
                                />
                                <div className="btn-wrapper mt-0">
                                  <button
                                    className="theme-btn-1 btn btn-block"
                                    type="submit"
                                  >
                                    Save Changes
                                  </button>
                                </div>
                              </form>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* PRODUCT TAB AREA END */}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgentDashboard
